import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DailyToken {
  success: boolean;
  token?: {
    symbol: string;
    name: string;
    market_cap: number;
    current_price: number;
    volume_24h: number;
    price_change_24h: number;
    price_change_7d: number;
  };
  score?: number;
  /** Neutral momentum tier (TOP_MOMENTUM | NOTABLE_MOVER | ON_THE_RADAR) */
  momentum_tier?: string;
  /** Display label for the momentum tier (e.g. "Top Momentum") */
  momentum_label?: string;
  /** Legacy rating field — now mirrors momentum_tier for backward compat. */
  rating?: string;
  breakdown?: {
    momentum: number;
    position: number;
    technical: number;
    fundamentals: number;
    sentiment: number;
    risk: number;
  };
  position_details?: {
    supplyValueHealth?: {
      fdvRatioScore?: number;
      supplyCirculationScore?: number;
      athRecoveryScore?: number;
      undervaluationScore?: number;
    };
  };
  reasons?: string[];
  narrative?: string;
  risk_level?: string;
  recommendation?: string;
  buy_signals?: Array<{
    type: string;
    strength: string;
    description: string;
  }>;
  /** Cached AI Reviewer verdict for the featured token (populates the Entry Timing Read). */
  reviewer?: {
    internal_verdict: string;
    display_label: string;
    overall_score: number | null;
    headline_reasoning: string | null;
    reviewed_at: string;
  } | null;
  filter_info?: {
    rejected_top_toss?: {
      symbol: string;
      score: number;
      reasons: string[];
    } | null;
  };
  selected_at?: string;
  message?: string;
  data?: {
    topCandidate?: {
      symbol: string;
      score: number;
      message?: string;
      rejection_reasons?: string[];
    };
  };
  thresholds_used?: {
    min_market_cap: number;
    max_market_cap: number;
    min_consideration_score: number;
    min_pick_score: number;
    hot_pick_score: number;
    strong_contender_score: number;
  };
}

export const useDailyToken = () => {
  return useQuery({
    queryKey: ['daily-token'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('select-daily-token');
      
      if (error) {
        console.error('Error fetching daily token:', error);
        throw error;
      }

      return data as DailyToken;
    },
    staleTime: 1000 * 60 * 60, // 1 hour - daily token doesn't change often
    refetchInterval: false
  });
};
